import React, { useContext, useEffect, useRef, useState } from "react";
import { CreateArtifactContext } from "../../../context/CreateArtifactContext";
import * as Yup from "yup";
import { JournalArtifactItems, SKJournalArtifact, ContentState } from "../../../models/SKArtifact";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigationBar } from "../../../context/NavigationContext";
import { EditPopupJornals } from "./EditPopupJornals";
import { AddStickyNote } from "./container/components/AddStickyNote";
import { AddTextAreaField } from "../../../components/AddTextAreaField";
import { useNavigate } from "react-router-dom";

export default function AddJournal() {
  const { contentData, artifactId, updateContentData, loading, saveData, isEditable } = useContext(CreateArtifactContext);
  const { setNavProps } = useNavigationBar();
  const [isValidForm, setIsValidForm] = useState(false);
  const [itemTitle, setItemTitle] = useState("");
  const [itemNotes, setItemNotes] = useState("");
  const [items, setItems] = useState<JournalArtifactItems[]>([]);
  var [isValidInput, setIsValidInput] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [editItem, setEditItem] = useState<JournalArtifactItems>();

  const navigate = useNavigate();
  useEffect(() => {
    setNavProps(prev => ({
      ...prev,
      backArrow: false,
      buttonTitle: "Done",
      title: "Add Journal",
      getActionItems: undefined,
      enableLink: isEditable ? isValidForm : false,
      onNext: isEditable ? submit : undefined,
      onClose: () => navigate(-1),
      onEdit: undefined,
    }));
  }, [isValidForm, setNavProps, isEditable]);

  const schema = Yup.object().shape({
    journalItems: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.string().required(),
          journalTitle: Yup.string().notRequired(),
          journalNote: Yup.string().required(),
        })
      )
      .min(1, "At least one journal item is required")
      .required(),
  });

  const handleSave = async () => {
    try {
      setIsValidInput(false);
      const newItem: JournalArtifactItems = {
        id: crypto.randomUUID(),
        journalTitle: itemTitle,
        journalNote: itemNotes,
      };
      if (newItem) {
        const updatedItems = [...items, newItem];
        setItems(updatedItems);
        updateContentData({ journalItems: updatedItems });
        setItemNotes("");
      }
      setIsValidInput(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  useEffect(() => {
    const validate = async () => {
      try {
        await schema.validate(contentData, { abortEarly: false });
        setIsValidForm(true);
      } catch (err) {
        setIsValidForm(false);
      }
    };
    validate();
  }, [contentData, schema]);

  useEffect(() => {
    if ((contentData as SKJournalArtifact).journalItems != null) {
      setItems((contentData as SKJournalArtifact).journalItems);
    }
  }, []);

  useEffect(() => {
    setIsValidInput(itemTitle.length >= 3 && itemNotes.length >= 3);
  }, [itemTitle, itemNotes]);

  const handleRemoveItem = (id: string) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    updateContentData({ journalItems: updatedItems });
  };

  const getItemById = (id: string): JournalArtifactItems | undefined => {
    return items.find(item => item.id === id);
  };

  const editItemPopup = (id: string) => {
    setEditItem(getItemById(id));
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const savePopup = async (editNotes: string) => {
    if (editItem) {
      const updatedItem = {
        ...editItem,
        journalNote: editNotes,
      };
      const updatedItems = items.map(item => (item.id === editItem.id ? updatedItem : item));
      setItems(updatedItems);
      await updateContentData({ journalItems: updatedItems });
      closePopup();
    }
  };

  const submit = async () => {
    await saveData();
    window.history.back();
  };

  return (
    <div className="flex flex-col mt-[12px] gap-[30px]">
      {isEditable && (
        <div>
          <AddTextAreaField
            name={"AddNote"}
            title={"Add Note"}
            note={"Type or paste the text."}
            placeholder={"Note"}
            index={1}
            height={150}
            charAllowed={150}
            value={itemNotes}
            onChange={async e => {
              setItemNotes(e.target.value);
            }}
            disabled={contentData.status !== ContentState.IN_REVIEW ? false : true}
          />
          <div className="flex flex-row w-[480px]">
            <button
              onClick={handleSave}
              className={
                (itemNotes.length ? "bg-black cursor-pointer" : "bg-gray-400 disabled-link") +
                " flex items-center justify-center px-3 py-1 text-sm font-normal text-white rounded-lg flex-shrink-0 w-[68px] h-[32px] ml-auto"
              }
            >
              Add
            </button>
          </div>
        </div>
      )}
      <div>
        <div className="flex items-center">
          {isEditable && <span className="content_count">2</span>}
          <span className="content_title">Notes Added</span>
        </div>
        <div className="notes-class-content">Note you add show up here, you can edit, reorder, and delete.</div>
        <div className="flex flex-wrap gap-[15px]">
          {items.map((note, index) => (
            <AddStickyNote key={index} text={note.journalNote!} onDelete={() => handleRemoveItem(note.id)} onEdit={() => editItemPopup(note.id)} />
          ))}
        </div>

        {isEditable && <EditPopupJornals isOpen={isPopupOpen} onClose={closePopup} onSave={savePopup} editNotes={editItem?.journalNote} />}
      </div>
    </div>
  );
}
